/**
 * useCanvas3DSync Hook - Synchronizes spatial canvas panel positions to 3D floor
 * Enables bidirectional communication between 2D canvas and 3D floor system
 */

import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import type { PanelState } from './panel-layout-store';
import type { LivingFloorInstance } from './living-floor';

export interface Canvas3DSyncOptions {
  floorRef: React.MutableRefObject<LivingFloorInstance | null>;
  panelStates: Map<string, PanelState>;
  cursorPos?: THREE.Vector3;
  activeUserIds?: Set<string>;
}

/**
 * Hook to sync spatial canvas state with 3D floor
 * Converts 2D panel coordinates to 3D floor space
 */
export const useCanvas3DSync = (options: Canvas3DSyncOptions) => {
  const {
    floorRef,
    panelStates,
    cursorPos = new THREE.Vector3(0, 0, 0),
    activeUserIds = new Set(),
  } = options;

  const lastSyncRef = useRef({
    panelPositions: [] as Array<{ x: number; y: number; z: number }>,
    cursorPos: cursorPos.clone(),
    activeUsers: [] as Array<{ x: number; y: number; z: number; intensity: number }>,
  });

  // Convert screen coordinates to 3D floor space
  const screenToFloorSpace = useCallback(
    (screenX: number, screenY: number): { x: number; z: number } => {
      // Normalize screen coordinates to [-1, 1] range
      const normalizedX = (screenX / window.innerWidth) * 2 - 1;
      const normalizedY = (screenY / window.innerHeight) * 2 - 1;

      // Map to floor bounds (-50 to 50)
      const floorX = normalizedX * 50;
      const floorZ = normalizedY * 50;

      return { x: floorX, z: floorZ };
    },
    []
  );

  // Update floor with panel positions
  const updateFloorPanels = useCallback(() => {
    if (!floorRef.current) return;

    const panelPositions = Array.from(panelStates.values()).map((panel) => {
      const floorSpace = screenToFloorSpace(panel.position.x, panel.position.y);

      return {
        x: floorSpace.x,
        // Z index becomes height above floor
        y: (panel.zIndex - 100) / 100,
        z: floorSpace.z,
      };
    });

    // Only update if positions changed significantly
    const positionsChanged =
      panelPositions.length !== lastSyncRef.current.panelPositions.length ||
      panelPositions.some(
        (p, i) =>
          Math.abs(p.x - lastSyncRef.current.panelPositions[i]?.x) > 1 ||
          Math.abs(p.z - lastSyncRef.current.panelPositions[i]?.z) > 1
      );

    if (positionsChanged) {
      floorRef.current.updatePanels(panelPositions);
      lastSyncRef.current.panelPositions = panelPositions;
    }
  }, [panelStates, screenToFloorSpace, floorRef]);

  // Update floor with cursor position
  const updateFloorCursor = useCallback(() => {
    if (!floorRef.current) return;

    const floorSpace = screenToFloorSpace(cursorPos.x, cursorPos.y);
    const floorCursorPos = new THREE.Vector3(floorSpace.x, 1, floorSpace.z);

    const cursorChanged =
      Math.abs(floorCursorPos.x - lastSyncRef.current.cursorPos.x) > 0.5 ||
      Math.abs(floorCursorPos.z - lastSyncRef.current.cursorPos.z) > 0.5;

    if (cursorChanged) {
      floorRef.current.updateCursor(floorCursorPos);
      lastSyncRef.current.cursorPos = floorCursorPos;
    }
  }, [cursorPos, screenToFloorSpace, floorRef]);

  // Update floor with active users
  const updateFloorActiveUsers = useCallback(() => {
    if (!floorRef.current) return;

    // For now, place active users at random positions on the floor
    // In a real implementation, this would come from PartyKit presence
    const activeUsers = Array.from(activeUserIds).map((userId, idx) => {
      const angle = (idx / activeUserIds.size) * Math.PI * 2;
      const radius = 20;

      return {
        x: Math.cos(angle) * radius,
        y: 1,
        z: Math.sin(angle) * radius,
        intensity: 1.0,
      };
    });

    floorRef.current.updateActiveUsers(activeUsers);
    lastSyncRef.current.activeUsers = activeUsers;
  }, [activeUserIds, floorRef]);

  // Sync panels whenever they change
  useEffect(() => {
    updateFloorPanels();
  }, [panelStates, updateFloorPanels]);

  // Sync cursor position (debounced)
  useEffect(() => {
    const syncTimer = setInterval(updateFloorCursor, 50);
    return () => clearInterval(syncTimer);
  }, [cursorPos, updateFloorCursor]);

  // Sync active users (less frequently)
  useEffect(() => {
    updateFloorActiveUsers();
    const syncTimer = setInterval(updateFloorActiveUsers, 1000);
    return () => clearInterval(syncTimer);
  }, [activeUserIds, updateFloorActiveUsers]);

  return {
    screenToFloorSpace,
    syncPanels: updateFloorPanels,
    syncCursor: updateFloorCursor,
    syncActiveUsers: updateFloorActiveUsers,
  };
};
